import { clsx } from "clsx";
import { type FC, useState } from "react";
import { useHover } from "react-aria";
import { Callout, CalloutIntent, Collapse, TriggerButton, TriggerButtonIntent, TriggerButtonSize } from "widgets";
import { humanReadableFileSize } from "widgets/utils";

import { Tile } from "@/modules/core/components/Tile.js";
import { FILE_STATUS } from "@/settings/settings.js";
import { FileIcon } from "@/ui-modules/core/FileIcon.js";
import tilesStyles from "@/ui-modules/core/styles/Tiles.module.scss";

import { FileUploadProgressIndicator } from "./FileUploadProgressIndicator.js";

interface FileUploadTileProps {
  key?: string | number;
  file: {
    fileName: string;
    fileSize?: number;
    creationDate?: number;
    lastModified?: number;
    targetLocation?: string;
  };
  uploadStatus: FILE_STATUS;
  progress: number;
  errors: string[];
  cancelUploadRequest: (file: FileUploadTileProps["file"]) => void;
  clearFile: (file: FileUploadTileProps["file"]) => void;
  hideIcon?: boolean;
  hideSize?: boolean;
}

const FileUploadTile: FC<FileUploadTileProps> = (props) => {
  const { size, unit } = humanReadableFileSize(props.file.fileSize, true);
  const creationDate = props.file.creationDate ? new Date(props.file.creationDate).toISOString() : undefined;
  const lastModifiedDate = props.file.lastModified ? new Date(props.file.lastModified).toISOString() : undefined;
  const { hoverProps, isHovered } = useHover({});
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const errors = Array.isArray(props.errors) ? props.errors : [props.errors];

  return (
    <div {...hoverProps}>
      <Tile
        style={{
          maxWidth: 500,
          margin: "0 auto 5px",
        }}
        title={props.file.fileName}
        subtitle={props.file.targetLocation}
        icon={
          props.hideIcon !== true ? (
            <span
              style={{
                display: "flex",
                flexDirection: "column",
                fontSize: "10px",
                alignItems: "center",
              }}
            >
              <FileIcon filename={props.file.fileName} size={"big"} />
              {!props.hideSize ? (
                <>
                  {size} {unit}
                </>
              ) : null}
            </span>
          ) : undefined
        }
        dates={{ created: creationDate, modified: lastModifiedDate }}
        footNote={
          (props.uploadStatus === FILE_STATUS.UPLOAD_FAILED ||
            props.uploadStatus === FILE_STATUS.UPLOAD_CANCELED ||
            props.uploadStatus === FILE_STATUS.VALIDATION_FAILED) &&
          (errors.length > 1 ? (
            <Callout key={`error`} intent={CalloutIntent.Danger}>
              <div style={{ display: "flex" }}>
                <div style={{ flex: 1 }}>{errors[0]}</div>
                <TriggerButton
                  label={isExpanded ? "Hide all errors" : "Show all errors"}
                  onPress={() => setIsExpanded(!isExpanded)}
                  icon={isExpanded ? "unfold_less_double" : "unfold_more_double"}
                  size={TriggerButtonSize.Small}
                />
              </div>
              <Collapse isExpanded={isExpanded}>
                {errors.slice(1).map((err, index) => (
                  <Callout key={`${index}-error`} intent={CalloutIntent.Danger} style={{ paddingLeft: 0 }}>
                    {err}
                  </Callout>
                ))}
              </Collapse>
            </Callout>
          ) : (
            <Callout intent={CalloutIntent.Danger}>{props.errors}</Callout>
          ))
        }
      >
        <div className={clsx(tilesStyles.action)}>
          {(props.uploadStatus === FILE_STATUS.UPLOAD_COMPLETE ||
            props.uploadStatus === FILE_STATUS.VALIDATION_COMPLETE) && (
            <TriggerButton
              intent={TriggerButtonIntent.Secondary}
              style={{ color: isHovered ? "#797c80" : "#66c2a5" }}
              icon={isHovered ? "close" : "check"}
              label={""}
              aria-label={"Close tile"}
              onPress={() => props.clearFile(props.file)}
            />
          )}
          {(props.uploadStatus === FILE_STATUS.UPLOAD_PENDING ||
            props.uploadStatus === FILE_STATUS.VALIDATION_WAITING ||
            props.uploadStatus === FILE_STATUS.VALIDATION_PENDING) && (
            <TriggerButton
              intent={TriggerButtonIntent.Secondary}
              style={{ color: "#797c80" }}
              icon={"close"}
              label={""}
              aria-label={"Close tile"}
              onPress={() => props.cancelUploadRequest(props.file)}
            />
          )}
          {(props.uploadStatus === FILE_STATUS.UPLOAD_FAILED ||
            props.uploadStatus === FILE_STATUS.UPLOAD_CANCELED ||
            props.uploadStatus === FILE_STATUS.VALIDATION_FAILED) && (
            <TriggerButton
              intent={TriggerButtonIntent.Secondary}
              style={{ color: "#f13d5b" }}
              icon={"close"}
              label={""}
              aria-label={"Close tile"}
              onPress={() => props.clearFile(props.file)}
            />
          )}
        </div>
        <FileUploadProgressIndicator progress={props.progress} uploadStatus={props.uploadStatus} />
      </Tile>
    </div>
  );
};

export { FileUploadTile };
